/**
 * Cliente da assinatura eletrônica de contratos.
 *
 * As chamadas da tela pública passam `skipAuthRedirect`: um 401 ali significa
 * "código de acesso errado", não sessão expirada. Sem a flag, o interceptor
 * global mandaria o paciente para a tela de login do sistema.
 */

import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import type {
  CidConsentRequestPayload,
  CidConsentSummary,
  ContractTemplate,
  CreateEnvelopePayload,
  EnvelopeDetail,
  MedicalRecordDelivery,
  MedicalRecordDeliveryPayload,
  PublicSignatureView,
  PublicValidation,
  SignatureEnvelope,
  SignatureMethod,
  SignerLink,
  TemplateVariableMeta,
} from '../types/signature';

const base = () => `${getApiUrl()}/clinic/signatures`;
const publicBase = () => `${getApiUrl()}/public/signature`;

const PUBLIC_OPTS = { skipAuthRedirect: true } as const;

/** O backend responde `{ success, error }`; sem isso a UI mostraria "Request failed with status code 409". */
function unwrapError(error: any, fallback: string): Error {
  const message = error?.response?.data?.error || error?.message || fallback;
  const wrapped = new Error(message);
  (wrapped as any).status = error?.response?.status;
  return wrapped;
}

// ===========================================================================
// Modelos de contrato
// ===========================================================================

export async function listContractTemplates(params?: {
  category?: string;
  search?: string;
  professional_id?: string;
  include_inactive?: boolean;
}): Promise<ContractTemplate[]> {
  const res = await axios.get(`${base()}/templates`, { params });
  return res.data?.data || [];
}

export async function getContractTemplate(id: string): Promise<ContractTemplate> {
  const res = await axios.get(`${base()}/templates/${id}`);
  return res.data?.data;
}

export async function getTemplateVariables(): Promise<{
  variables: Record<string, TemplateVariableMeta>;
  categories: Record<string, string>;
}> {
  const res = await axios.get(`${base()}/template-variables`);
  return res.data?.data;
}

export async function createContractTemplate(
  payload: Partial<ContractTemplate>,
): Promise<ContractTemplate> {
  try {
    const res = await axios.post(`${base()}/templates`, payload);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível salvar o modelo.');
  }
}

export async function updateContractTemplate(
  id: string,
  payload: Partial<ContractTemplate>,
): Promise<ContractTemplate> {
  try {
    const res = await axios.put(`${base()}/templates/${id}`, payload);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível salvar o modelo.');
  }
}

export async function duplicateContractTemplate(id: string): Promise<ContractTemplate> {
  const res = await axios.post(`${base()}/templates/${id}/duplicate`);
  return res.data?.data;
}

export async function reorderContractTemplates(ids: string[]): Promise<void> {
  await axios.post(`${base()}/templates/reorder`, { ids });
}

export async function archiveContractTemplate(id: string): Promise<void> {
  await axios.delete(`${base()}/templates/${id}`);
}

export async function previewContractTemplate(
  bodyHtml: string,
  vars?: Record<string, string>,
): Promise<{ html: string; variables_used: string[] }> {
  const res = await axios.post(`${base()}/templates/preview`, { body_html: bodyHtml, vars });
  return res.data?.data;
}

// ===========================================================================
// Envelopes
// ===========================================================================

export async function listEnvelopes(params?: {
  status?: string;
  patient_id?: string;
  search?: string;
  limit?: number;
}): Promise<SignatureEnvelope[]> {
  const res = await axios.get(`${base()}/envelopes`, { params });
  return res.data?.data || [];
}

export async function getEnvelope(id: string): Promise<EnvelopeDetail> {
  const res = await axios.get(`${base()}/envelopes/${id}`);
  return res.data?.data;
}

export async function createEnvelope(payload: CreateEnvelopePayload): Promise<EnvelopeDetail> {
  try {
    const res = await axios.post(`${base()}/envelopes`, payload);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível criar o envelope.');
  }
}

export async function updateEnvelope(
  id: string,
  payload: Partial<CreateEnvelopePayload>,
): Promise<EnvelopeDetail> {
  try {
    const res = await axios.put(`${base()}/envelopes/${id}`, payload);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível atualizar o envelope.');
  }
}

export async function sendEnvelope(id: string): Promise<EnvelopeDetail> {
  try {
    const res = await axios.post(`${base()}/envelopes/${id}/send`);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível enviar para assinatura.');
  }
}

export async function getSignerLink(envelopeId: string, signerId: string): Promise<SignerLink> {
  try {
    const res = await axios.get(`${base()}/envelopes/${envelopeId}/signers/${signerId}/link`);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível gerar o link de assinatura.');
  }
}

export async function cancelEnvelope(id: string): Promise<EnvelopeDetail> {
  try {
    const res = await axios.post(`${base()}/envelopes/${id}/cancel`);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível cancelar o envelope.');
  }
}

export function envelopePdfUrl(id: string): string {
  return `${base()}/envelopes/${id}/pdf`;
}

/** Abre o PDF final numa aba nova, levando o JWT — a rota é autenticada. */
export async function openEnvelopePdf(id: string): Promise<void> {
  const res = await axios.get(envelopePdfUrl(id), { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  // Revogar na hora cancelaria a aba que acabou de abrir.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ===========================================================================
// Recibo de entrega de prontuário
// ===========================================================================

export async function listMedicalRecordDeliveries(patientId: string): Promise<MedicalRecordDelivery[]> {
  try {
    const res = await axios.get(`${base()}/medical-record-deliveries`, {
      params: { patient_id: patientId },
    });
    return res.data?.data || [];
  } catch (error) {
    throw unwrapError(error, 'Não foi possível carregar as entregas de prontuário.');
  }
}

export async function createMedicalRecordDelivery(
  payload: MedicalRecordDeliveryPayload,
): Promise<{ delivery: MedicalRecordDelivery; envelope: EnvelopeDetail }> {
  try {
    const res = await axios.post(`${base()}/medical-record-deliveries`, payload, { timeout: 120_000 });
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível registrar a entrega.');
  }
}

export async function cancelMedicalRecordDelivery(id: string): Promise<MedicalRecordDelivery> {
  try {
    const res = await axios.post(`${base()}/medical-record-deliveries/${id}/cancel`);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível cancelar a entrega.');
  }
}

// ===========================================================================
// Autorização do CID no atestado
// ===========================================================================

export async function getCidConsent(certificateId: string): Promise<CidConsentSummary> {
  try {
    const res = await axios.get(`${base()}/certificate-cid-consent/${certificateId}`);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível consultar a autorização do CID.');
  }
}

export async function requestCidConsent(
  certificateId: string,
  payload: CidConsentRequestPayload,
): Promise<{ consent: CidConsentSummary; envelope: EnvelopeDetail }> {
  try {
    const res = await axios.post(`${base()}/certificate-cid-consent/${certificateId}`, payload, {
      timeout: 120_000,
    });
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível pedir a autorização do CID.');
  }
}

export async function cancelCidConsent(certificateId: string): Promise<CidConsentSummary> {
  try {
    const res = await axios.post(`${base()}/certificate-cid-consent/${certificateId}/cancel`);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível cancelar o pedido.');
  }
}

// ===========================================================================
// Tela pública do signatário
// ===========================================================================

export async function getPublicSignatureView(token: string): Promise<PublicSignatureView> {
  try {
    const res = await axios.get(`${publicBase()}/${encodeURIComponent(token)}`, PUBLIC_OPTS);
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível abrir o documento.');
  }
}

export async function requestAccessCode(
  token: string,
): Promise<{ required: boolean; sent: boolean; destination: string | null }> {
  try {
    const res = await axios.post(
      `${publicBase()}/${encodeURIComponent(token)}/access-code`,
      {},
      PUBLIC_OPTS,
    );
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível enviar o código.');
  }
}

export async function confirmIdentity(
  token: string,
  accessCode?: string,
): Promise<PublicSignatureView> {
  try {
    const res = await axios.post(
      `${publicBase()}/${encodeURIComponent(token)}/confirm-identity`,
      { access_code: accessCode },
      PUBLIC_OPTS,
    );
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível confirmar sua identidade.');
  }
}

export async function registerDocumentView(token: string, documentId: string): Promise<void> {
  try {
    await axios.post(
      `${publicBase()}/${encodeURIComponent(token)}/view/${documentId}`,
      {},
      PUBLIC_OPTS,
    );
  } catch {
    // Evidência complementar: a assinatura em si já registra o acesso.
  }
}

export async function signDocument(
  token: string,
  payload: {
    method: SignatureMethod;
    signature_image?: string;
    typed_name?: string;
    certificate?: string;
    certificate_passphrase?: string;
  },
): Promise<PublicSignatureView> {
  try {
    const res = await axios.post(
      `${publicBase()}/${encodeURIComponent(token)}/sign`,
      payload,
      { ...PUBLIC_OPTS, timeout: 120_000 },
    );
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível registrar sua assinatura.');
  }
}

export async function refuseSignature(
  token: string,
  reason?: string,
): Promise<PublicSignatureView> {
  try {
    const res = await axios.post(
      `${publicBase()}/${encodeURIComponent(token)}/refuse`,
      { reason },
      PUBLIC_OPTS,
    );
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível registrar a recusa.');
  }
}

export async function downloadSignedDocument(token: string): Promise<void> {
  const res = await axios.get(`${publicBase()}/${encodeURIComponent(token)}/pdf`, {
    ...PUBLIC_OPTS,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function validatePublicDocument(
  envelopeId: string,
  code?: string,
): Promise<PublicValidation> {
  try {
    const res = await axios.get(`${publicBase()}/validate/${envelopeId}`, {
      ...PUBLIC_OPTS,
      params: code ? { code } : undefined,
    });
    return res.data?.data;
  } catch (error) {
    throw unwrapError(error, 'Não foi possível validar o documento.');
  }
}
